import Tippy, { TippyProps } from "@tippy.js/react";
import * as React from "react";
import styled, { css } from "styled-components";
import { Placement } from "tippy.js";
import { Icon } from "./Icon";
import { style } from "./style";
import { focusOutline } from "./utils/focusOutlineStyle";

const StyledTooltip = styled(Tippy)<{ $theme: "light" | "dark" }>`
  padding: ${style.vars.space["2"]} ${style.vars.space["3"]};
  height: auto;

  /*
    To overwrite specific styles on child elements, see the Tippy themes
    documentation: https://atomiks.github.io/tippyjs/themes/#tippy-elements
  */

  ${(props) => {
    if (props.$theme === "dark") {
      return css`
        background-color: ${style.colors.grey.dark};

        &[x-placement^="top"] .tippy-arrow {
          border-top-color: ${style.colors.grey.dark};
        }
        &[x-placement^="left"] .tippy-arrow {
          border-left-color: ${style.colors.grey.dark};
        }
        &[x-placement^="right"] .tippy-arrow {
          border-right-color: ${style.colors.grey.dark};
        }
        &[x-placement^="bottom"] .tippy-arrow {
          border-bottom-color: ${style.colors.grey.dark};
        }
      `;
    } else {
      return css`
        background-color: ${style.colors.white};
        border: 1px solid ${style.colors.grey.light};
        color: ${style.colors.grey.dark};
        box-shadow: ${style.vars.boxShadow.top};

        && .tippy-arrow {
          border-top-color: ${style.colors.white};
          border-bottom-color: ${style.colors.white};
        }
      `;
    }
  }}
`;

const InlineTooltipChildren = styled.span<{ $disabled: boolean }>`
  all: unset;

  color: inherit;
  border-bottom: ${style.vars.borderWidth.base} dotted;

  ${(props) => {
    if (!props.$disabled) {
      return css`
        &:active,
        &:focus {
          outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
          outline-offset: 3px;
        }
      `;
    }
  }}
`;

export type TooltipPlacement = Placement;

/**
 * TODO: Remove this.
 */
export const TooltipChildren = styled.div`
  &:active,
  &:focus {
    ${focusOutline()}
  }
`;

type TooltipProps = Omit<TippyProps, "className" | "theme"> & {
  theme?: "light" | "dark";
};

type TooltipHelpProps = {
  content: TippyProps["content"];
  theme?: "light" | "dark";
  placement?: TooltipPlacement;
};

type TooltipInlineProps = {
  content: TippyProps["content"];
  theme?: "light" | "dark";
  disabled?: boolean;
  placement?: TooltipPlacement;
};

const TooltipContent = styled(TooltipChildren)`
  display: inline-block;
  margin-left: ${style.vars.whitespace.small};
  height: 16px;
  width: 16px;
  line-height: 1;
`;

export const Tooltip = (props: React.PropsWithChildren<TooltipProps>): JSX.Element => {
  const { theme = "dark", ...rest } = props;

  return <StyledTooltip $theme={theme} arrow={true} {...rest} />;
};
Tooltip.displayName = "Tooltip";

const TooltipHelp: React.FC<TooltipHelpProps> = (props) => {
  const { content, theme = "dark", placement = "top" } = props;

  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <Tooltip
      aria="describedby"
      theme={theme}
      content={content}
      onShow={() => setIsHovered(true)}
      onHide={() => setIsHovered(false)}
      placement={placement}
    >
      <TooltipContent>{isHovered ? <Icon icon="tooltip-active" /> : <Icon icon="tooltip-default" />}</TooltipContent>
    </Tooltip>
  );
};
TooltipHelp.displayName = "Tooltip.Help";
Tooltip.Help = TooltipHelp;

const TooltipInline: React.FC<TooltipInlineProps> = (props) => {
  const { content, children, theme = "dark", disabled = false, placement = "top" } = props;

  return (
    <Tooltip aria="describedby" boundary="window" content={content} theme={theme} enabled={!disabled} placement={placement}>
      <InlineTooltipChildren $disabled={disabled} tabIndex={disabled ? -1 : 0}>
        {children}
      </InlineTooltipChildren>
    </Tooltip>
  );
};
TooltipInline.displayName = "Tooltip.Inline";
Tooltip.Inline = TooltipInline;
